import { Translation, useTranslation } from 'react-i18next';
import React, { useMemo, useCallback, useState, useRef } from 'react';
import { useForm, FieldValues, Control, UseFormSetValue } from 'react-hook-form';
import produce from 'immer';
import cx from 'clsx';
import { QueryClientProvider } from 'react-query';

import { IconRcStreamPrizeCash, IconRcStreamPrizeCustom } from '@/assets/draw';
import { Segmented } from '@/components';
import { Channel, RichTextEditor, RichTextEditorHandle } from '@/components/RichTextEditor';
import { useSwitch, useCommentEligibility, useChannelPick } from '@/hooks';
import { devLog, queryClient } from '@/utils';
import { SUPPORTED_LOCALE_TYPE } from '@/locales';

import { PostFooter } from '../PostFooter';
import { CashPrize } from './CashPrize';
import { CustomPrize } from './CustomPrize';
import style from './index.module.scss';

type ControlProps = Control<FieldValues, object>;

const PRIZE_TYPES = [
  {
    label: (
      <div className={style.segItem}>
        <IconRcStreamPrizeCash className={style.segIcon} width={12} height={12} />
        <span>
          <Translation>{t => t('LuckyDraw.zj3bol')}</Translation>
        </span>
      </div>
    ),
    value: 'cash',
    render: ({
      control,
      disabled,
      setValue,
      isL2Pay,
      handleL2PayHide,
      handleL2Pay,
      extraPaymentParams,
      refreshKey,
      hiddenEligibilityFields,
    }: {
      control: ControlProps;
      disabled: boolean;
      setValue: UseFormSetValue<any>;
      isL2Pay?: boolean;
      handleL2PayHide?: () => void;
      handleL2Pay?: (data: Object) => Promise<void>;
      extraPaymentParams?: Object;
      refreshKey?: string;
      hiddenEligibilityFields?: string[];
    }) => (
      <CashPrize
        control={control}
        isL2Pay={isL2Pay!}
        extraPaymentParams={extraPaymentParams}
        turnOff={handleL2PayHide!}
        onSubmit={handleL2Pay!}
        disabled={disabled}
        setValue={setValue}
        key={refreshKey}
        hiddenEligibilityFields={hiddenEligibilityFields}
      />
    ),
  },
  {
    label: (
      <div className={style.segItem}>
        <IconRcStreamPrizeCustom className={style.segIcon} width={12} height={12} />
        <span>
          <Translation>{t => t('LuckyDraw.1jgpqjn')}</Translation>
        </span>
      </div>
    ),
    value: 'custom',
    render: ({
      control,
      disabled,
      setValue,
      refreshKey,
      hiddenEligibilityFields,
    }: {
      control: ControlProps;
      disabled: boolean;
      setValue: UseFormSetValue<any>;
      refreshKey?: string;
      hiddenEligibilityFields?: string[];
    }) => (
      <CustomPrize
        control={control}
        disabled={disabled}
        setValue={setValue}
        key={refreshKey}
        hiddenEligibilityFields={hiddenEligibilityFields}
      />
    ),
  },
];

type LuckyDrawProps = {
  onConfirm: (data: EditorSubmitContentData) => Promise<void>;
  disabled?: boolean;
  extraPaymentParams?: Object;
  className?: string;
  language?: SUPPORTED_LOCALE_TYPE;
  refreshKey?: string;
  hiddenEligibilityFields?: string[];
  channel?: Channel;
  hideChannelPicker?: boolean;
};

export const LuckyDraw = ({
  onConfirm,
  disabled = false,
  extraPaymentParams,
  className,
  language,
  refreshKey,
  hiddenEligibilityFields,
  channel,
  hideChannelPicker,
}: LuckyDrawProps) => {
  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    formState: { isValid },
    reset,
    setValue,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      duration: 24 * 3600,
      permissions: {
        has_web3_id: true,
      },
    } as any,
  });

  const [prize, setPrize] = useState(PRIZE_TYPES[0].value);
  const { on: isLoading, turn: setLoading } = useSwitch();
  const { on: isL2Pay, turnOn: showL2Pay, turnOff: hideL2Pay } = useSwitch();
  const { on: isEditorInValid, turn: setInvalid } = useSwitch();
  const { channelId } = useChannelPick(channel);

  const restDrawData = useRef<EditorSubmitContentData>();
  const [commentEligibility] = useCommentEligibility();
  const editorRef = useRef<RichTextEditorHandle>(null);

  const prizeType = useMemo(() => PRIZE_TYPES.find(x => x.value === prize), [prize]);

  const handlePrizeChange = useCallback(
    v => {
      reset();
      setPrize(v);
    },
    [reset],
  );

  const onSubmit = useCallback(
    async data => {
      try {
        setLoading(true);
        const params: EditorSubmitContentData = {
          type: 'draw',
          content: '',
          permissions: commentEligibility,
          channel_id: channelId,
        };

        params.draw_data = produce(data, (draft: any) => {
          data.prize_count = Math.floor(data.prize_count);
          if (data.prize_value) {
            data.prize_value = Number(data.prize_value) * 10000;
          }

          draft.permissions = Object.keys(draft.permissions).reduce((m, n) => {
            if (Array.isArray(draft.permissions[n])) {
              // 这里注意，处理的很糙，选中且输入有值
              if (draft.permissions[n][0] && draft.permissions[n][1]) {
                const num = Number(draft.permissions[n][1]);
                m[n] = isNaN(num) ? draft.permissions[n][1] : num;
              }
            } else if (draft.permissions[n]) {
              m[n] = true;
            }
            return m;
          }, {} as any);

          draft.operations = {};

          // draft.operations = Object.keys(draft.operations).reduce(
          //   (m, n) => {
          //     if (draft.operations[n]) {
          //       m[n] = null;
          //     }

          //     return m;
          //   },
          //   {} as Record<string, null>,
          // );
        }) as any;

        const textData = await editorRef.current?.getSubmitContent();

        if (textData) {
          Object.assign(params, {
            content: textData.textContent,
            entities: {
              ...(textData.mentionsList?.length ? { mentions: textData.mentionsList } : {}),
              ...(textData.hashtagsList?.length ? { tags: textData.hashtagsList } : {}),
              ...(textData.urlsWidthIndices?.length ? { url: textData.urlsWidthIndices } : {}),
              ...(textData.slashesList.length ? { channels: textData.slashesList } : {}),
            },
          });
        }

        devLog('submit draw content', params);

        if (prizeType?.value === 'cash') {
          restDrawData.current = params;
          showL2Pay();
        } else {
          await onConfirm(params);
        }
      } catch (error) {
        setLoading(false);
      }
    },
    [setLoading, prizeType?.value, showL2Pay, onConfirm, commentEligibility, channelId],
  );

  const handleL2Pay = useCallback(
    async (txData: Object) => {
      if (restDrawData.current) {
        const params = produce(restDrawData.current, draft => {
          draft = {
            ...draft,
            ...txData,
          };
          return draft;
        });
        await onConfirm(params);
      }
    },
    [onConfirm],
  );

  const handleL2PayHide = useCallback(() => {
    setLoading(false);
    hideL2Pay();
  }, [setLoading, hideL2Pay]);

  return (
    <QueryClientProvider client={queryClient}>
      <div className={cx(style.container, className)}>
        <div className={style.draw}>
          <div className={style.title}>{t('LuckyDraw.1v3e43k')}</div>
          <RichTextEditor
            maxLength={600}
            editorClassName={style.textEditor}
            placeholderClassName={style.editorStyle}
            wrapperClassName={style.editorWrapper}
            toolbar={false}
            footer={false}
            disableUploadImage
            placeholder={t('LuckyDraw.70foxz')}
            disabled={disabled}
            disableCard
            onInvalidChange={setInvalid}
            ref={editorRef}
            language={language}
          />
          <Segmented
            color="form"
            hasSplit={false}
            options={PRIZE_TYPES}
            value={prize}
            onChange={handlePrizeChange}
            radius={15}
            className={style.drawSegmented}
            shadow
          />

          {prizeType?.render({
            control,
            disabled,
            setValue,
            isL2Pay,
            handleL2PayHide,
            handleL2Pay,
            extraPaymentParams,
            refreshKey,
            hiddenEligibilityFields,
          })}
        </div>
        <PostFooter
          disabled={disabled || isEditorInValid || !isValid}
          isLoading={isLoading}
          onSubmit={handleSubmit(onSubmit)}
          hiddenEligibilityFields={hiddenEligibilityFields}
          hideChannelPicker={hideChannelPicker}
        />
      </div>
    </QueryClientProvider>
  );
};
